<template>
  <div class="context-menu">
    <woot-modal
      v-if="isCannedResponseModalOpen && showCannedResponseOption"
      :show.sync="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <add-canned-modal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <woot-button
      icon="chevron-down"
      color-scheme="white"
      variant="smooth"
      size="tiny-plus"
      @click="handleContextMenuClick"
    />
    <div
      v-if="isOpen && !isCannedResponseModalOpen"
      v-on-clickaway="handleContextMenuClick"
      class="dropdown-pane dropdown-pane--open"
      :class="`dropdown-pane--${menuPosition}`"
      :style="dropdownPosition"
    >
      <woot-dropdown-menu>
        <woot-dropdown-item v-if="showDelete" style="display: none;">
          <woot-button
            class="menu-item"
            variant="clear"
            color-scheme="alert"
            width="16"
            height="16"
            icon="delete"
            @click="handleDelete"
          >
            {{ $t('CONVERSATION.CONTEXT_MENU.DELETE') }}
          </woot-button>
        </woot-dropdown-item>
        <woot-dropdown-item v-if="showReplyTo">
          <woot-button
            class="menu-item"
            variant="clear"
            icon="arrow-reply-menu"
            width="16"
            height="16"
            view-box="0 0 16 16"
            color-scheme="secondary"
            @click="handleReplyTo"
          >
            {{ $t('CONVERSATION.CONTEXT_MENU.REPLY_TO') }}
          </woot-button>
        </woot-dropdown-item>
        <woot-dropdown-item v-if="showCopy">
          <woot-button
            class="menu-item"
            width="16"
            height="16"
            variant="clear"
            icon="copy-menu"
            color-scheme="secondary"
            @click="handleCopy"
          >
            {{ $t('CONVERSATION.CONTEXT_MENU.COPY') }}
          </woot-button>
        </woot-dropdown-item>
        <woot-dropdown-item v-if="showCannedResponseOption">
          <woot-button
            class="menu-item"
            variant="clear"
            width="16"
            height="16"
            icon="comment-add"
            color-scheme="secondary"
            @click="showCannedResponseModal"
          >
            {{ $t('CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE') }}
          </woot-button>
        </woot-dropdown-item>
        <woot-dropdown-item v-if="visibleTranslate">
          <woot-button
            class="menu-item"
            variant="clear"
            width="16"
            height="16"
            icon="translate"
            color-scheme="secondary"
            @click="handleTranslate"
          >
            {{ $t('CONVERSATION.CONTEXT_MENU.TRANSLATE') }}
          </woot-button>
        </woot-dropdown-item>
      </woot-dropdown-menu>
    </div>
  </div>
</template>
<script>
import alertMixin from 'shared/mixins/alertMixin';
import { mixin as clickaway } from 'vue-clickaway';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';

import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { ACCOUNT_EVENTS } from '../../../helper/AnalyticsHelper/events';

export default {
  components: {
    AddCannedModal,
    WootDropdownMenu,
    WootDropdownItem,
  },
  mixins: [alertMixin, clickaway, messageFormatterMixin],
  props: {
    messageContent: {
      type: String,
      default: '',
    },
    messageId: {
      type: Number,
      default: 0,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    showCopy: {
      type: Boolean,
      default: false,
    },
    showReplyTo: {
      type: Boolean,
      default: false,
    },
    showDelete: {
      type: Boolean,
      default: false,
    },
    menuPosition: {
      type: String,
      default: 'left',
    },
    showCannedResponseOption: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      visibleTranslate: false,
      dropdownPosition: {},
    };
  },
  computed: {
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
  },
  methods: {
    /**
     * Devuelve un objeto con la posición top del menu de opciones (dropdown-pane).
     * @returns {Object} Objeto con los estilos CSS a aplicar.
     */
    getDropdownTopPosition() {
      // Posibles opciones que pueden mostrarse en el dropdown, no se agregó la opcion de delete porque no se esta mostrando actualmente
      //  en el producto, en caso querer mostrar esa opción hay que agregar this.showDelete al arreglo de opciones.
      const dropdownOptions = [
        this.showReplyTo,
        this.showCannedResponseOption,
        this.showCopy,
        this.visibleTranslate,
      ];
      const optionHeight = 40;
      const optionMarginBottom = 2;
      const dropdownPadding = 16;
      const borderTopAndBottom = 2;
      // Se calcula la altura del dropdown.
      const dropdownHeight =
        dropdownOptions.reduce(
          (sum, option) =>
            sum + (option ? optionHeight + optionMarginBottom : 0),
          0
        ) +
        dropdownPadding +
        borderTopAndBottom;
      // Altura del botón que muestra el dropdown.
      const dropdownButtonWidth = 28;
      // Altura de la lista de mensajes.
      const messageListTopPosition = document
        .querySelector('.conversation-panel')
        .getClientRects()[0].top;
      // Altura del elemento li que contine la burbuja de mensajes
      const messageTopPosition = document
        .querySelector(`#li-${this.messageId}`)
        .getClientRects()[0].top;

      // Se hace un calculo para saber si el dropdown al desplegarse verticalmente se verá completamente en la pantalla o no.
      const difference =
        messageTopPosition - dropdownHeight - messageListTopPosition;
      const dropdownTop =
        difference > 0 ? { top: 'unset' } : { top: `${dropdownButtonWidth}px` };
      return dropdownTop;
    },
    /**
     * Devuelve un objeto con la posición top del menu de opciones (dropdown-pane).
     * @returns {Object} Objeto con los estilos CSS a aplicar.
     */
    getDropdownLeftPosition() {
      // Se obtiene el ancho del contenedor de la burbuja
      const wrapMessageWidth = document.querySelector(`#wrap-${this.messageId}`)
        ?.offsetWidth;
      // Se calcula el ancho del dropdown dependiendo del texto de alguna de las opciones que puede mostrar.
      const dropdownWidth = this.showCannedResponseOption
        ? 281
        : this.showReplyTo
        ? 136
        : 108;

      // Espacio mínimo de separacion entre el dropdown y el extremo del MessageList.
      const dropdownMinSpaceToBorder = 15;
      // Ancho del botón que muestra el dropdown.
      const dropdownButtonWidth = 28;
      // Margen aplicado a contenedor de la burbuja para que se separe del borde del MessageList.
      const bubbleWrapperMargin = 20;
      // Ancho ocupado por el boton que muestra el dropdown y el margen que lo separa de la burbuja.
      const dropdownButtoWidthAndMargin = 30;
      // Se calcula si el dropdown se sale de la pantalla.
      const difference =
        wrapMessageWidth +
        bubbleWrapperMargin +
        dropdownButtoWidthAndMargin -
        dropdownWidth;
      // Almacena el valor del atributo left pertenecienta al dropdown.
      let finalLeftPosition = 0;
      // Calcula el valor final del atributo left dependiendo si el mensaje es del contacto o no
      switch (this.menuPosition) {
        // --- Para las burbujas del contacto ---
        case 'left':
          finalLeftPosition =
            difference > dropdownMinSpaceToBorder
              ? -(dropdownWidth - dropdownButtonWidth)
              : -(wrapMessageWidth + dropdownMinSpaceToBorder);
          break;
        // --- Para las burbujas del agente y del bot ---
        default:
          finalLeftPosition =
            difference >= dropdownMinSpaceToBorder
              ? 0
              : difference - dropdownMinSpaceToBorder;
      }

      return { left: `${finalLeftPosition}px` };
    },
    /**
     * Devuelve un objeto con los datos de posición del menu de opciones.
     * @returns {Object} Objeto con los estilos CSS a aplicar.
     */
    calculateDropdownPosition() {
      this.dropdownPosition = {
        ...this.getDropdownLeftPosition(),
        ...this.getDropdownTopPosition(),
      };
    },
    handleContextMenuClick() {
      this.calculateDropdownPosition();
      this.$emit('toggle', !this.isOpen);
    },
    /**
     * Emite eventos para manejar la respuesta a un mensaje y alternar el estado de visibilidad.
     *
     * @returns {void} No devuelve ningún valor.
     */
    handleReplyTo() {
      this.$emit('reply-to', this.message);
      this.$emit('toggle', false);
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      this.showAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.$emit('toggle', false);
    },
    handleDelete() {
      this.$emit('delete');
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.$emit('toggle', false);
    },
    showCannedResponseModal() {
      this.$track(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },

    handleTranslate() {
      this.$emit('translate');
      this.handleContextMenuClick();
    },
  },
};
</script>
<style lang="scss" scoped>
.menu-item {
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-normal);
  line-height: var(--space-medium);
  text-align: left;
}
.dropdown-pane {
  box-shadow: none;
  bottom: 2.7rem;
  border-radius: var(--space-smaller-plus);
  top: auto;
  left: 0;
  border: 1px solid var(--conversa2-neutral-100-color);
}
.dropdown-pane--left {
  display: inline-table;
}
.dropdown-pane--right {
  display: inline-table;
}
.button {
  &.clear {
    &.secondary {
      color: var(--conversa2-neutral-800-color);
      font-size: var(--font-size-default);
      line-height: var(--space-medium);

      &:hover {
        color: var(--conversa2-neutral-800-color);
        background-color: var(--conversa2-neutral-50-color);
      }
    }
  }
}
</style>
